/**
 * Frontend debug logger.
 *
 * Events are buffered for 300ms then POSTed to /api/debug-log which appends
 * them to `frontend_debug.txt` by default. Pass a `channel` to route to a
 * dedicated file (`frontend_<channel>.txt`) so noisy subsystems don't drown
 * each other.
 *
 * Two exports:
 *   - debugLog(tag, data)                   // default channel → frontend_debug.txt
 *   - createChannelLogger(channel)          // dedicated file → frontend_<channel>.txt
 *
 * Logging is disabled in production and when not running on localhost.
 */

const _enabled =
  typeof process !== "undefined" &&
  process.env.NEXT_PUBLIC_DEBUG_LOG === "true" &&
  typeof window !== "undefined" &&
  (window.location.hostname === "localhost" ||
    window.location.hostname.startsWith("192.168."));

interface Entry {
  tag: string;
  [k: string]: unknown;
}

const _buffers = new Map<string, Entry[]>(); // channel → entries buffer
const _timers = new Map<string, ReturnType<typeof setTimeout>>();

function flush(channel: string) {
  const buf = _buffers.get(channel);
  if (!buf || buf.length === 0) return;
  _buffers.set(channel, []);
  const timer = _timers.get(channel);
  if (timer) {
    clearTimeout(timer);
    _timers.delete(channel);
  }

  try {
    console.debug(`[frontend-debug:${channel || "default"}]`, buf);
  } catch {}

  fetch("/api/debug-log", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    keepalive: true,
    body: JSON.stringify({
      ts: new Date().toISOString(),
      channel: channel || undefined,
      entries: buf,
    }),
  }).catch(() => {});
}

function scheduleFlush(channel: string) {
  if (_timers.has(channel)) return;
  const t = setTimeout(() => flush(channel), 300);
  _timers.set(channel, t);
}

/** Default channel (frontend_debug.txt). */
export function debugLog(tag: string, data: Record<string, unknown>) {
  if (!_enabled) return;
  const channel = "";
  const buf = _buffers.get(channel) ?? [];
  buf.push({ tag, ...data });
  _buffers.set(channel, buf);
  scheduleFlush(channel);
}

/**
 * Return a channel-scoped logger that writes to frontend_<channel>.txt.
 * Use for dedicated subsystems (alerts, streaming, billing, etc) so their
 * logs don't mix with the general debug stream.
 *
 * Example:
 *   const log = createChannelLogger("alert_display");
 *   log("node:mount", { cellIndex: 5, outputsCount: 3 });
 */
export function createChannelLogger(channel: string) {
  return function log(tag: string, data: Record<string, unknown> = {}) {
    if (!_enabled) return;
    const buf = _buffers.get(channel) ?? [];
    buf.push({ tag, ...data });
    _buffers.set(channel, buf);
    scheduleFlush(channel);
  };
}

export function clearDebugLog(channel?: string) {
  const qs = channel ? `?channel=${encodeURIComponent(channel)}` : "";
  return fetch(`/api/debug-log${qs}`, { method: "DELETE" });
}
